import React, { ReactElement, useMemo } from 'react'

import { Tag, Tooltip, Txt } from 'src/components/atoms'
import { SegmentedControls } from 'src/components/atoms/navigations/SegmentedControls'

import { CompassRoleEntity } from 'src/types/entities/CompassRole'

// undefined は未選択状態を表す
type CompassRoleId = number | undefined

function CompassRoleCount({ roleName, count }: { roleName: string; count: number }) {
  return (
    <li className="tw-full-justify tw-gap-s0">
      <Txt sizeType="s" className="tw-text-inlineText-secondary">
        {roleName}
      </Txt>
      <Txt className="tw-font-bold tw-text-[#EDEEF2]">{count}</Txt>
    </li>
  )
}

function CompassRoleTooltip(props: {
  userCompassRoleIds: CompassRoleId[]
  compassRoles: CompassRoleEntity[]
  children: ReactElement
}) {
  const { userCompassRoleIds, compassRoles, children } = props

  // ユーザーがいるcompassRoleのみ表示する
  const filteredCompassRoles = useMemo(
    () => compassRoles.filter(compassRole => userCompassRoleIds.includes(compassRole.id)),
    [userCompassRoleIds, compassRoles]
  )

  const hasUnselectedUser = useMemo(
    () => userCompassRoleIds.includes(undefined),
    [userCompassRoleIds]
  )

  return (
    <Tooltip
      content={
        <div>
          <Txt sizeType="s" className="tw-text-[#91939B]">
            ROOV compass権限
          </Txt>
          <ul className="tw-grid tw-gap-s-2 tw-mt-s-2">
            {hasUnselectedUser && (
              <CompassRoleCount
                roleName="未選択"
                count={userCompassRoleIds.filter(id => id === undefined).length}
              />
            )}
            {filteredCompassRoles.map(compassRole => (
              <CompassRoleCount
                key={compassRole.id}
                roleName={compassRole.displayName}
                count={userCompassRoleIds.filter(id => id === compassRole.id).length}
              />
            ))}
          </ul>
        </div>
      }
      placement="bottom"
      showIcon={false}
      // ユーザーが0件の場合はTooltipを表示しない
      disabled={userCompassRoleIds.length === 0}
    >
      {children}
    </Tooltip>
  )
}

function ControlChildren(props: {
  text: string
  isSelected: boolean
  userCompassRoleIds: CompassRoleId[]
  compassRoles: CompassRoleEntity[]
}) {
  const { text, isSelected, userCompassRoleIds, compassRoles } = props

  return (
    <div>
      <CompassRoleTooltip userCompassRoleIds={userCompassRoleIds} compassRoles={compassRoles}>
        <div className="tw-inline-flex tw-items-center tw-gap-s-2">
          <div>{text}</div>
          <Tag size="s" type={isSelected ? 'primary' : 'secondary'}>
            {userCompassRoleIds.length}
          </Tag>
        </div>
      </CompassRoleTooltip>
    </div>
  )
}

type Props = {
  internalUserCompassRoleIds: CompassRoleId[]
  externalUserCompassRoleIds: CompassRoleId[]
  compassRoles: CompassRoleEntity[]
  selectedId: 'internal' | 'external'
  onSelect: (id: 'internal' | 'external') => void
  className?: string
}

export function ProjectAssignSegmentedControls({
  internalUserCompassRoleIds,
  externalUserCompassRoleIds,
  compassRoles,
  selectedId,
  onSelect,
  className,
}: Props) {
  return (
    <div className={className}>
      <SegmentedControls
        options={[
          {
            id: 'internal',
            children: (
              <ControlChildren
                text="社内ユーザー"
                compassRoles={compassRoles}
                isSelected={selectedId === 'internal'}
                userCompassRoleIds={internalUserCompassRoleIds}
              />
            ),
          },
          {
            id: 'external',
            children: (
              <ControlChildren
                text="社外ユーザー"
                compassRoles={compassRoles}
                isSelected={selectedId === 'external'}
                userCompassRoleIds={externalUserCompassRoleIds}
              />
            ),
          },
        ]}
        selectedId={selectedId}
        onSelect={id => onSelect(id as 'internal' | 'external')}
        size="s"
      />
    </div>
  )
}
